/**
 * Which locale and time zone the app runs with (docs/ops-protocol.md,
 * "Phase 11"). The app follows the viewer like an iOS app follows the device,
 * within the languages it ships: a browser language whose ICU data the app
 * carries (`locales` in its spec, default English) is used with its region
 * (`en-GB` → British dates), anything else falls back to the first shipped
 * language rather than to ICU's root locale. `?locale=fr-FR` on the app URL
 * overrides the browser, like the "App Language" option of an Xcode scheme.
 *
 * Shared by the page and the Node side (tests), so no DOM or Node APIs.
 */

const TAG = /^[A-Za-z]{2,3}(?:[-_][A-Za-z0-9]{2,8})*$/;

/** The language subtag of a BCP 47 tag, lowercased; null for junk. */
export function languageOf(tag: string): string | null {
  const trimmed = tag.trim();
  if (!TAG.test(trimmed)) return null;
  return trimmed.split(/[-_]/)[0]!.toLowerCase();
}

/** `fr_FR`, `FR-fr` → `fr-FR`; script subtags title-cased (`zh-Hant-TW`). */
export function canonicalTag(tag: string): string {
  const parts = tag.trim().split(/[-_]/);
  return parts
    .map((part, index) => {
      if (index === 0) return part.toLowerCase();
      if (part.length === 4) return part[0]!.toUpperCase() + part.slice(1).toLowerCase();
      return part.toUpperCase();
    })
    .join('-');
}

/**
 * The locale to run with: the override when given, else the first preferred
 * language the app ships (`shipped` holds language tags; `*` ships every
 * language), else the first shipped language itself (`en` → `en`), else
 * nothing (the module keeps its default, `en_001`).
 */
export function chooseLocale(
  preferred: readonly string[],
  shipped: readonly string[] | undefined,
  override?: string | null,
): string | null {
  if (override && languageOf(override)) return canonicalTag(override);
  const languages = (shipped ?? ['en']).map((tag) => (tag === '*' ? '*' : languageOf(tag))).filter((l): l is string => l !== null);
  const all = languages.includes('*');
  for (const tag of preferred) {
    const language = languageOf(tag);
    if (language && (all || languages.includes(language))) return canonicalTag(tag);
  }
  const first = languages.find((l) => l !== '*');
  return first ?? null;
}

/** The viewer's IANA time zone (`Europe/Paris`), or null when the runtime cannot say. */
export function currentTimeZone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone && /^[A-Za-z0-9_+\-/]+$/.test(zone) ? zone : null;
  } catch {
    return null;
  }
}
